import { Alert, Code, Drawer, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons';
import { DetailRow, StatusBadge } from '../../../components';
import { formatDate } from './format-date';
import { JsonSection } from './json-section';
import type { JobTask } from '../../../types/job-task';

interface JobTaskDrawerProps {
  task: JobTask | null;
  onClose: () => void;
}

export function JobTaskDrawer({ task, onClose }: JobTaskDrawerProps) {
  return (
    <Drawer
      opened={Boolean(task)}
      onClose={onClose}
      position="right"
      size="xl"
      padding="lg"
      title={<Title order={4}>Task details</Title>}
    >
      {task ? (
        <Stack spacing="lg">
          <Stack spacing={4}>
            <Group spacing="sm">
              <Text size="sm" weight={600}>
                {task.type}
              </Text>
              <StatusBadge status={task.status} />
            </Group>
            <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
              {task._id}
            </Text>
          </Stack>
          <Paper withBorder p="lg">
            <Group spacing="xl" align="flex-start">
              <DetailRow label="Origin" value={task.origin || '—'} />
              <DetailRow label="Attempt" value={task.attempt} />
              <DetailRow label="Created" value={formatDate(task.createdAt)} />
              <DetailRow label="Started" value={formatDate(task.startedAt)} />
              <DetailRow label="Finished" value={formatDate(task.finishedAt)} />
            </Group>
          </Paper>
          {task.error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title={`${task.error.code} (attempt ${task.error.attempt})`}
            >
              <Stack spacing="xs">
                <Text size="sm">{task.error.message}</Text>
                {task.error.details && (
                  <Code block sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {JSON.stringify(task.error.details, null, 2)}
                  </Code>
                )}
              </Stack>
            </Alert>
          )}
          <JsonSection title="Payload" data={task.payload} defaultOpened />
          <JsonSection title="Metadata" data={task.metadata} />
        </Stack>
      ) : null}
    </Drawer>
  );
}
